import { IconButtonProps } from "@material-ui/core";
import {
  makeStyles,
  PaginationProps as MacawPaginationProps
} from "@saleor/macaw-ui";
import { formatThousandInteger } from "@saleor/utils/number";
import { parse as parseQs } from "qs";
import React, { useEffect, useState } from "react";

import { ListSettings } from "../../types";
import MyTypography from "../MyTypography";
import RowNumberSelect from "../RowNumberSelect";
import Skeleton from "../Skeleton";
import { BIG_ROW_NUMBER_SET } from "../TablePagination/constants";
import TablePaginationAction from "./TablePaginationAction";

const useStyles = makeStyles(
  theme => ({
    wrapper: {
      background: "rgba(212, 244, 248, 0.8)"
    },
    root: {
      display: "flex",
      justifyContent: "space-between",
      fontSize: 12
    },
    index: {
      display: "flex",
      alignItems: "center",
      "& .MuiTypography-root": {
        color: theme.palette.grey[900],
        "&.total": {
          marginLeft: "4px",
          fontSize: "14px"
        }
      }
    },
    textPagination: {
      fontSize: "14px"
    },
    btnAction: {
      display: "flex",
      alignItems: "center"
    },
    btnActionFixed: {
      right: 20,
      position: "fixed"
    },
    pagination: {
      display: "flex",
      justifyContent: "flex-end",
      color: theme.palette.grey[900],
      "& >div": {
        fontSize: 12,
        lineHeight: "18px"
      },
      "& .MuiSelect-root": {
        color: theme.palette.grey[900]
      },
      "& .MuiButtonBase-root": {
        marginLeft: "10px",
        borderRadius: "6px",
        width: "2.4rem",
        height: "2rem",
        background: "#FFFFFF",
        boxShadow:
          "0px 1px 1px rgba(0, 0, 0, 0.1), 0px 0px 0px 1px rgba(0, 0, 0, 0.24)",
        border: "none",
        "&:hover": {
          boxShadow:
            "0px 1px 1px rgba(0, 0, 0, 0.1), 0px 0px 0px 1px rgba(0, 0, 0, 0.5)"
        },
        "&:active": {
          boxShadow:
            "0px 1px 1px rgba(0, 0, 0, 0.1), 0px 0px 0px 1px rgba(0, 0, 0, 0.24), 0px 0px 0px 3px rgba(1, 178, 237, 0.36)"
        },
        "& svg": {
          fontSize: "16px"
        }
      },
      "& .MuiButtonBase-root.Mui-disabled": {
        boxShadow: "0px 0px 0px 1px rgba(0, 0, 0, 0.08)",
        "& svg": {
          color: theme.palette.grey[500]
        }
      }
    }
  }),
  { name: "Pagination" }
);

export type ListSettingsUpdate = <T extends keyof ListSettings>(
  key: T,
  value: ListSettings[T]
) => void;

export interface PaginationProps
  extends Omit<MacawPaginationProps, "labels" | "rowNumber"> {
  component?: React.ElementType;
  colSpan?: number;
  settings?: ListSettings;
  rowNumberSet?: number[];
  totalCount?: number | null;
  latestPageItemNumber?: number | null;
  onUpdateListSettings?: ListSettingsUpdate;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  nextIconButtonProps?: Partial<IconButtonProps>;
  backIconButtonProps?: Partial<IconButtonProps>;
  isShowIndex?: boolean;
  selectColor?: string;
  noOfRowsThai?: boolean;
  isBackFistPage?: boolean;
  isNextLastPage?: boolean;
  onPreviousFirstPage?: () => void;
  onNextLastPage?: () => void;
}

export const TablePaginationCustom: React.FC<PaginationProps> = ({
  settings,
  totalCount,
  latestPageItemNumber,
  hasPreviousPage,
  hasNextPage,
  nextIconButtonProps,
  backIconButtonProps,
  disabled,
  isShowIndex,
  selectColor,
  noOfRowsThai,
  isBackFistPage,
  isNextLastPage,
  onNextLastPage,
  onPreviousFirstPage,
  onNextPage,
  onPreviousPage,
  onUpdateListSettings
}) => {
  const classes = useStyles();
  const [startIndex, setStartIndex] = useState(1);
  const [endIndex, setEndIndex] = useState(
    Math.min(settings?.rowNumber, totalCount)
  );
  const handleNextPage = () => {
    onNextPage();
    setStartIndex(startIndex + settings?.rowNumber);
    setEndIndex(Math.min(endIndex + settings.rowNumber, totalCount));
  };
  const handleNextLastPage = () => {
    onNextLastPage();
    setStartIndex(totalCount + 1 - latestPageItemNumber);
    setEndIndex(totalCount);
  };
  const handlePreviousPage = () => {
    onPreviousPage();
    setStartIndex(startIndex - settings.rowNumber);
    setEndIndex(startIndex - 1);
  };
  const handlePreviousFirstPage = () => {
    onPreviousFirstPage();
    setStartIndex(1);
    setEndIndex(Math.min(settings?.rowNumber, totalCount));
  };

  useEffect(() => {
    const qs = parseQs(location.search.substr(1));
    if (
      !hasPreviousPage &&
      !qs?.after &&
      !qs?.before &&
      totalCount &&
      !disabled
    ) {
      setStartIndex(1);
      setEndIndex(Math.min(settings?.rowNumber, totalCount));
    }
  }, [settings?.rowNumber, totalCount, hasPreviousPage, disabled]);

  return (
    <div className={classes.root}>
      {isShowIndex ? (
        <div className={classes.index}>
          {totalCount && startIndex && endIndex ? (
            <>
              <MyTypography
                className={classes.textPagination}
                variant="caption"
              >{`${startIndex} - ${endIndex}`}</MyTypography>
              <MyTypography variant="caption" className="total">
                of {formatThousandInteger(totalCount)}
              </MyTypography>
            </>
          ) : (
            <Skeleton />
          )}
        </div>
      ) : null}
      {!isShowIndex && settings?.rowNumber && (
        <RowNumberSelect
          choices={BIG_ROW_NUMBER_SET}
          rowNumber={settings?.rowNumber}
          onChange={value => onUpdateListSettings("rowNumber", value)}
          selectColor={selectColor}
          noOfRowsThai={noOfRowsThai}
        />
      )}
      <div className={classes.btnAction}>
        {isShowIndex && settings?.rowNumber && (
          <RowNumberSelect
            choices={BIG_ROW_NUMBER_SET}
            rowNumber={settings?.rowNumber}
            onChange={value => onUpdateListSettings("rowNumber", value)}
            selectColor={selectColor}
            noOfRowsThai={noOfRowsThai}
          />
        )}
        <TablePaginationAction
          hasNextPage={hasNextPage}
          hasPreviousPage={hasPreviousPage}
          disabled={disabled}
          backIconButtonProps={backIconButtonProps}
          nextIconButtonProps={nextIconButtonProps}
          onNextPage={handleNextPage}
          onNextLastPage={handleNextLastPage}
          onPreviousPage={handlePreviousPage}
          onPreviousFirstPage={handlePreviousFirstPage}
          className={classes.pagination}
          isBackFistPage={isBackFistPage}
          isNextLastPage={isNextLastPage}
        />
      </div>
    </div>
  );
};

export default React.memo(TablePaginationCustom);
